/**
 * AuditLogPage
 *
 * Read-only audit trail viewer for super_admin and store_owner.
 * Lists write actions performed in the system with filters by date,
 * user, action and entity type.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, RefreshCw, FileText, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';

function badgeColorForAction(action) {
  if (!action) return 'bg-gray-100 text-gray-700';
  if (action.startsWith('BULK_DELETE') || action === 'DELETE') return 'bg-red-100 text-red-800';
  if (action.startsWith('BULK_') || action === 'UPDATE' || action === 'PATCH') return 'bg-amber-100 text-amber-800';
  if (action === 'CREATE') return 'bg-emerald-100 text-emerald-800';
  if (action === 'IMPERSONATE' || action === 'STOP_IMPERSONATION') return 'bg-purple-100 text-purple-800';
  return 'bg-gray-100 text-gray-700';
}

function formatTimestamp(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function AuditLogPage({ onBack, currentUser }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    user_id: '',
    action: '',
    entity_type: '',
    start_date: '',
    end_date: '',
  });
  const [filterOptions, setFilterOptions] = useState({ users: [], actions: [], entity_types: [] });
  const [limit, setLimit] = useState(100);

  const isSuperAdmin = currentUser?.role === 'super_admin';

  const loadFilters = async () => {
    try {
      const resp = await axios.get(`${API}/audit-logs/filters`);
      setFilterOptions(resp.data || { users: [], actions: [], entity_types: [] });
    } catch (err) {
      console.error('Failed to load audit filter options:', err);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      params.append('limit', String(limit));
      const resp = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setItems(resp.data?.items || []);
      setTotal(resp.data?.total || 0);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      toast.error(err.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFilters();
    loadLogs();
  }, []); // eslint-disable-line

  const resetFilters = () => {
    setFilters({ user_id: '', action: '', entity_type: '', start_date: '', end_date: '' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="page-container max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onBack} data-testid="audit-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                <FileText className="w-7 h-7" /> Audit Trail
              </h1>
              <p className="text-sm text-gray-600">
                {isSuperAdmin ? 'Cross-store activity log' : 'Activity log for your store'} · retained for 1 year
              </p>
            </div>
          </div>
          <Button onClick={loadLogs} variant="outline" data-testid="audit-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-base">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <div>
                <Label className="text-xs">User</Label>
                <Select
                  value={filters.user_id || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, user_id: v === 'all' ? '' : v })}
                >
                  <SelectTrigger data-testid="audit-filter-user">
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    <SelectItem value="all">All users</SelectItem>
                    {filterOptions.users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {(u.name || u.email || u.id) + (u.role ? ` (${u.role})` : '')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Action</Label>
                <Select
                  value={filters.action || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, action: v === 'all' ? '' : v })}
                >
                  <SelectTrigger data-testid="audit-filter-action">
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    <SelectItem value="all">All actions</SelectItem>
                    {filterOptions.actions.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Entity</Label>
                <Select
                  value={filters.entity_type || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, entity_type: v === 'all' ? '' : v })}
                >
                  <SelectTrigger data-testid="audit-filter-entity">
                    <SelectValue placeholder="All entities" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    <SelectItem value="all">All entities</SelectItem>
                    {filterOptions.entity_types.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Start date</Label>
                <Input
                  type="date"
                  data-testid="audit-filter-start-date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">End date</Label>
                <Input
                  type="date"
                  data-testid="audit-filter-end-date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={loadLogs} data-testid="audit-apply-filters-btn">Apply Filters</Button>
              <Button variant="outline" onClick={resetFilters} data-testid="audit-reset-filters-btn">Reset</Button>
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-xs">Limit</Label>
                <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
                  <SelectTrigger className="w-24" data-testid="audit-limit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Audit Entries</span>
              <span className="text-sm text-gray-500 font-normal">
                Showing {items.length} of {total}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && items.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-center text-gray-500 py-8" data-testid="audit-empty">
                No audit entries match the current filters.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="audit-table">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="py-2 pr-3">Time</th>
                      <th className="py-2 pr-3">User</th>
                      <th className="py-2 pr-3">Action</th>
                      <th className="py-2 pr-3">Entity</th>
                      <th className="py-2 pr-3">Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className="border-b hover:bg-gray-50 align-top">
                        <td className="py-2 pr-3 whitespace-nowrap text-gray-700">{formatTimestamp(it.timestamp)}</td>
                        <td className="py-2 pr-3">
                          <div className="font-medium">{it.userName || it.userEmail || '-'}</div>
                          <div className="text-xs text-gray-500">
                            {it.userRole}{it.userEmail ? ` · ${it.userEmail}` : ''}
                          </div>
                          {it.impersonatedBy && (
                            <div className="text-xs text-purple-700 flex items-center gap-1 mt-1">
                              <UserCog className="w-3 h-3" />
                              via {it.impersonatedBy.name || it.impersonatedBy.email}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <Badge className={badgeColorForAction(it.action)}>{it.action}</Badge>
                        </td>
                        <td className="py-2 pr-3 text-gray-700">
                          <div>{it.entityType}</div>
                          {it.changes?.item_name && (
                            <div className="text-xs text-gray-500">{it.changes.item_name}</div>
                          )}
                          {it.changes?.customer_name && (
                            <div className="text-xs text-gray-500">{it.changes.customer_name}</div>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs">
                          {it.changes?.changes && it.changes.changes.length > 0 ? (
                            <div className="space-y-1 max-w-xs">
                              {it.changes.changes.slice(0, 5).map((change, idx) => (
                                <div key={idx} className="bg-gray-50 rounded px-2 py-1">
                                  <span className="font-medium text-gray-700">{change.field}:</span>
                                  <span className="text-red-600 line-through ml-1">
                                    {typeof change.old === 'object' ? JSON.stringify(change.old) : String(change.old ?? '-')}
                                  </span>
                                  <span className="text-gray-400 mx-1">→</span>
                                  <span className="text-green-600">
                                    {typeof change.new === 'object' ? JSON.stringify(change.new) : String(change.new ?? '-')}
                                  </span>
                                </div>
                              ))}
                              {it.changes.changes.length > 5 && (
                                <div className="text-gray-500 italic">+{it.changes.changes.length - 5} more changes</div>
                              )}
                            </div>
                          ) : it.changes?.items_changed && it.changes.items_changed.length > 0 ? (
                            <div className="space-y-1 max-w-xs">
                              {it.changes.items_changed.slice(0, 3).map((item, idx) => (
                                <div key={idx} className="bg-gray-50 rounded px-2 py-1">
                                  <div className="font-medium text-gray-700">{item.item_name}</div>
                                  {item.changes.map((ch, chIdx) => (
                                    <div key={chIdx} className="text-xs">
                                      {ch.field}: <span className="text-red-600">${ch.old}</span> → <span className="text-green-600">${ch.new}</span>
                                    </div>
                                  ))}
                                </div>
                              ))}
                              {it.changes.items_changed.length > 3 && (
                                <div className="text-gray-500 italic">+{it.changes.items_changed.length - 3} more items</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">No details</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AuditLogPage;
